/*
 * SESSION END ⇒ a VISIBLE redirect to sign-in with the return path preserved
 * (Duncan rule CMT-2026-09-03-026, dispatched BOARDROOM-2026-09-03-064610 §3).
 *
 * The app must NEVER sit open-but-detached: before this module, an id-token
 * expiry in an open tab left the whole shell rendering while every
 * loadSession() read returned null — the profile wizard showed its faint
 * "You're not signed in" note as a RESTING state and a real signed-in user's
 * edits died on Save (Duncan's live report). Now every dead-session detection
 * point funnels here:
 *
 *   1. ROUTE ENTRY + IN-APP EXPIRY — useSessionExpiryRedirect (mounted once in
 *      the authed shell) checks on every navigation, at the token's own expiry
 *      moment, and on tab wake.
 *   2. The WIZARD's no-session load/save paths (ProfileWizard) — the banner
 *      stays only as the transient pre-redirect state.
 *   3. DataProvider's ApiAuthError exhaustion (via redirectToSignIn).
 *
 * COMPOSITION WITH SELF-HEAL (never fight it): a redirect fires only AFTER a
 * refresh attempt fails for a NON-transient reason. An expired record still
 * carrying a refresh token is first exchanged for a fresh session
 * (REFRESH_TOKEN_AUTH — the BOARDROOM-105741 idiom); success means the user
 * never sees sign-in at all. A network failure during refresh is NOT proof the
 * session is dead (capability-claim discipline) — the app keeps its honest
 * degraded states and retries at the next detection point.
 *
 * The redirect itself touches NOTHING in localStorage: guardian device-local
 * child records, profile extras and drafts all survive the round-trip.
 */
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { AuthFlowError, refreshSession } from './cognitoAuth'
import { loadSession, peekExpiredSession, storeSession } from './session'

export const RETURN_TO_PARAM = 'returnTo'

/**
 * Fixed resolution base for return-path validation. Deliberately NOT
 * window.location: the output is ALWAYS re-assembled as pathname+search+hash
 * (a same-origin path by construction), so the base's only job is being the
 * origin an honest relative path must resolve BACK to — and a fixed base
 * keeps the choke point independent of location stubs/state.
 */
const SANITIZE_BASE = 'https://my-trumin.invalid'

/**
 * Validate a candidate return path. Same-origin SPA paths only — resolved
 * through the WHATWG URL parser at this ONE choke point, because prefix
 * string checks are bypassable (adversarial review F1): browsers treat '\'
 * as '/' in special schemes and strip TAB/CR/LF, so '/\evil.com',
 * '/\/evil.com', '/<TAB>/evil.com', '/<LF>/evil.com', '/<CR>/evil.com' and
 * '/<TAB>//evil.com' all resolve to https://evil.com/ despite starting with
 * a single '/'. Resolving against a fixed base and requiring the origin to
 * survive rejects every such escape. The auth callback is excluded, and '/'
 * itself maps to null (the post-sign-in default already lands there).
 */
export function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/')) return null
  let u: URL
  try {
    u = new URL(raw, SANITIZE_BASE)
  } catch {
    return null
  }
  if (u.origin !== SANITIZE_BASE) return null // escaped the base origin → hostile
  if (u.pathname.startsWith('/auth/callback')) return null
  const path = `${u.pathname}${u.search}${u.hash}`
  return path === '/' ? null : path
}

/** The current SPA location (path + query + hash) as a return path. */
export function currentReturnPath(
  loc: { pathname: string; search: string; hash: string } = window.location,
): string {
  return `${loc.pathname}${loc.search}${loc.hash}`
}

/** The sign-in URL carrying the return path. Login renders at '/' for a
 *  signed-out user; its finish() honours ?returnTo (validated). */
export function signInUrlWithReturn(returnTo: string): string {
  const clean = sanitizeReturnTo(returnTo)
  return clean ? `/?${RETURN_TO_PARAM}=${encodeURIComponent(clean)}` : '/'
}

// One navigation per page lifetime: several detectors (route watcher, wizard,
// DataProvider) can conclude "dead" in the same tick — the first wins, the
// rest are no-ops instead of a replace() storm.
let redirecting = false

/** VISIBLE redirect to sign-in, return path preserved. Idempotent per page. */
export function redirectToSignIn(returnTo: string = currentReturnPath()): void {
  if (redirecting) return
  redirecting = true
  window.location.replace(signInUrlWithReturn(returnTo))
}

/** Test seam — jsdom never actually navigates, so the guard must be resettable. */
export function resetSessionExpiryForTests(): void {
  redirecting = false
  healInFlight = null
}

export type SessionHealth =
  | 'live' // an unexpired session exists (possibly just healed)
  | 'healed' // was expired; the refresh token minted a fresh session
  | 'dead' // absent / expired with no refresh token / refresh REJECTED
  | 'indeterminate' // refresh failed for a transient (network) reason — NOT proof of death

let healInFlight: Promise<SessionHealth> | null = null

/**
 * Ground-truth the session, healing if possible. Single-flight: concurrent
 * detectors share one refresh attempt. NEVER more aggressive than the status
 * quo — it only ever ADDS a session (storeSession on heal); it clears nothing.
 */
export function ensureLiveSession(): Promise<SessionHealth> {
  if (!healInFlight) {
    healInFlight = probeAndHeal().finally(() => {
      healInFlight = null
    })
  }
  return healInFlight
}

async function probeAndHeal(): Promise<SessionHealth> {
  if (loadSession()) return 'live'
  const stale = peekExpiredSession()
  if (!stale?.refreshToken) return 'dead'
  const username =
    (stale.claims['cognito:username'] as string | undefined) ||
    stale.claims.email ||
    stale.claims.sub
  try {
    const fresh = await refreshSession(username, stale.refreshToken)
    storeSession(fresh) // the whole app reads the healed session from here on
    return 'healed'
  } catch (err) {
    // A network blip is evidence about THAT call, not about the session —
    // never dump a user to sign-in because they were briefly offline.
    if (err instanceof AuthFlowError && err.code === 'Network') return 'indeterminate'
    return 'dead'
  }
}

/**
 * Heal if possible; redirect to sign-in (return path preserved) when the
 * session is genuinely absent/expired/unrefreshable. Resolves true when a
 * live session exists after the call.
 */
export async function healSessionOrRedirect(): Promise<boolean> {
  const health = await ensureLiveSession()
  if (health === 'dead') {
    redirectToSignIn()
    return false
  }
  return health === 'live' || health === 'healed'
}

/**
 * Mounted ONCE inside the authed shell: watches for session end and enforces
 * the visible-redirect rule at (a) every route entry, (b) the token's own
 * expiry moment (proactive timer → the refresh usually heals BEFORE anything
 * breaks), and (c) tab wake after a sleep that outlived the token.
 */
export function useSessionExpiryRedirect(): void {
  const location = useLocation()
  const checkingRef = useRef(false)

  useEffect(() => {
    let disposed = false
    let timer: number | undefined

    const armTimer = () => {
      if (disposed) return
      window.clearTimeout(timer)
      const s = loadSession()
      if (!s) return
      // +500ms so the check runs on the DEAD side of the boundary; capped to
      // the max signed 32-bit delay (larger overflows to an immediate fire).
      const delay = Math.min(Math.max(s.expiresAt - Date.now() + 500, 1_000), 2 ** 31 - 1)
      timer = window.setTimeout(() => void check(), delay)
    }

    const check = async () => {
      if (disposed || checkingRef.current) return
      checkingRef.current = true
      try {
        await healSessionOrRedirect()
      } finally {
        checkingRef.current = false
      }
      armTimer()
    }

    const wake = () => {
      if (document.visibilityState !== 'visible') return
      void check()
    }

    void check() // route entry (and initial mount)
    window.addEventListener('focus', wake)
    document.addEventListener('visibilitychange', wake)
    return () => {
      disposed = true
      window.clearTimeout(timer)
      window.removeEventListener('focus', wake)
      document.removeEventListener('visibilitychange', wake)
    }
  }, [location.pathname])
}
